import {
  Estimate,
  EstimateService,
  EstimateStatus,
  SessionEstimate,
  SessionEstimateItem,
  SessionEstimateItemService,
  SessionEstimateItemUnitType,
  SessionEstimateService,
  RateScheduleService,
  TCommercialId,
  TCreateUsageEstimateItemRequest,
  TEstimateId,
  TProjectId,
  TRateScheduleSummary,
  TSessionActivityId,
  TSessionEstimateId,
  TSessionEstimateItemId,
  TSessionId,
  TUpdateEstimateRequest,
  TUpdateSessionEstimateItemRequest,
  TUpdateUsageEstimateItemRequest,
  TUsageEstimateId,
  TUsageEstimateItemId,
  TUsageId,
  UsageEstimate,
  UsageEstimateItem,
  UsageEstimateItemService,
  UsageEstimateService,
} from '@talon/web/lib/api';
import { DateString, Nullable } from '@talon/web/src/types';
import { getSessionActivityItems } from '@talon/web/src/utils/sessionActivityItems';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually
import { byNewest } from '../../utils/stableOrder';

const estimateRelations = 'session_estimates,usage_estimates,project';

/** What a number on the Estimates screen belongs to, for showing it's being recalculated */
export const recalcKey = {
  /** The estimate's totals */
  estimate: 'estimate',
  /** A session or usage table's totals */
  table: (kind: 'session' | 'usage', id: string) => `${kind}:${id}`,
  /** Every row's computed values in a session or usage table */
  rows: (kind: 'session' | 'usage', id: string) => `${kind}-rows:${id}`,
  /** One row's subtotal and total */
  item: (id: string) => `item:${id}`,
  /** One row's rate */
  itemRate: (id: string) => `item-rate:${id}`,
};

export class EstimateStore extends BaseStore {
  currentEstimate: Estimate | undefined = undefined;
  estimateList: Estimate[] = [];
  isLoading = false;
  isSaving = false;

  // Temp Estimates (used by Session and Usage Estimators for live estimates)
  tempEstimate: Estimate | undefined = undefined;
  tempSessionEstimates: Record<TSessionId, SessionEstimate> = {};
  tempSessionEstimateItems: Record<TSessionId, Record<TSessionActivityId, SessionEstimateItem[]>> = {};
  tempUsageEstimates: Record<TCommercialId, UsageEstimate> = {};
  tempUsageEstimateItems: Record<TCommercialId, Record<TUsageId, UsageEstimateItem[]>> = {};

  // Numbers being recalculated, by what they belong to (see recalcKey) - counted, as saves can overlap
  recalculating = new Map<string, number>();

  // Quick Estimate
  quickEstimateTotal = 0;
  quickEstimateTotalPlusFringe = 0;

  // The contracts Talon prices with, oldest first
  rateSchedules: TRateScheduleSummary[] = [];
  private rateSchedulesRequest?: Promise<void>;
  // The contract the quick estimate was priced with
  quickEstimateRateScheduleId: string | undefined = undefined;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      currentEstimate: observable,
      estimateList: observable,
      isLoading: observable,
      isSaving: observable,
      tempEstimate: observable,
      tempSessionEstimates: observable,
      tempSessionEstimateItems: observable,
      tempUsageEstimates: observable,
      tempUsageEstimateItems: observable,
      quickEstimateTotal: observable,
      quickEstimateTotalPlusFringe: observable,
      rateSchedules: observable,
      quickEstimateRateScheduleId: observable,
      recalculating: observable,
      canEditCurrent: computed,
      lastSessionEstimateDate: computed,
      setCurrentEstimate: action,
      setTempEstimate: action,
      setEstimateList: action,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  /** Whether any of these numbers (see recalcKey) are being recalculated */
  isRecalculating = (...keys: string[]) => keys.some((key) => (this.recalculating.get(key) ?? 0) > 0);

  /** Mark numbers (see recalcKey) as being recalculated, until the function it returns is called */
  private startRecalculating = (keys: string[]) => {
    runInAction(() => keys.forEach((key) => this.recalculating.set(key, (this.recalculating.get(key) ?? 0) + 1)));
    return () =>
      runInAction(() =>
        keys.forEach((key) => {
          const count = (this.recalculating.get(key) ?? 1) - 1;
          if (count > 0) this.recalculating.set(key, count);
          else this.recalculating.delete(key);
        })
      );
  };

  /** A whole table's numbers, and the estimate's totals */
  private tableKeys = (kind: 'session' | 'usage', id: string) => [
    recalcKey.rows(kind, id),
    recalcKey.table(kind, id),
    recalcKey.estimate,
  ];

  /** Whether saving these values re-derives a usage item's rate: the row sends every value back, so compare */
  private repricesRate = (itemId: string, changes: TUpdateUsageEstimateItemRequest) => {
    const item = this.currentEstimate?.usage_estimates?.flatMap((ue) => ue.items ?? []).find((i) => i.id === itemId);
    return (
      !!changes.resetToDefault?.length ||
      (!!item &&
        ((changes.type !== undefined && changes.type !== item.type) ||
          (changes.performerType !== undefined && changes.performerType !== item.performer_type) ||
          (changes.units !== undefined && changes.units !== item.units) ||
          (changes.roleId !== undefined && changes.roleId !== item.role_id)))
    );
  };

  /** One row's numbers, its table's totals and the estimate's */
  private itemKeys = (kind: 'session' | 'usage', itemId: string, rate = false) => {
    const tables: { id: string; items?: { id: string }[] }[] =
      (kind === 'session' ? this.currentEstimate?.session_estimates : this.currentEstimate?.usage_estimates) ?? [];
    const table = tables.find((t) => t.items?.some((item) => item.id === itemId));
    return [
      recalcKey.item(itemId),
      ...(rate ? [recalcKey.itemRate(itemId)] : []),
      ...(table ? [recalcKey.table(kind, table.id)] : []),
      recalcKey.estimate,
    ];
  };

  /**
   * Estimates
   */

  get canEditCurrent(): boolean {
    return !!this.currentEstimate?.project_id && this.currentEstimate?.status === EstimateStatus.Draft;
  }

  setCurrentEstimate(estimate?: Estimate) {
    this.currentEstimate = estimate;
  }

  setEstimateList(estimateList: Estimate[]) {
    this.estimateList = estimateList;
  }

  listEstimates = async (projectId: TProjectId, relations?: string) => {
    if (!projectId) return;
    // TODO implement pagination
    try {
      runInAction(() => (this.isLoading = true));

      const { estimates } = await this.q(
        EstimateService.listEstimates(projectId, undefined, undefined, undefined, relations)
      );
      runInAction(() => {
        this.setEstimateList([...estimates].sort(byNewest));
        this.isLoading = false;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  getEstimate = async (estimateId?: TEstimateId, relations?: string) => {
    if (!estimateId) return;
    try {
      const { estimate } = await this.q(EstimateService.getEstimate(estimateId, relations));
      return estimate as Estimate;
    } catch (e) {
      this.handleError(e);
    }
  };

  getEstimateByTitle = async (title?: string, relations?: string, isHidden?: boolean) => {
    if (!title) return;
    try {
      const { estimate } = await this.q(
        EstimateService.getEstimateByTitle(title, relations, isHidden ? 'true' : 'false')
      );
      return estimate as Estimate;
    } catch (e) {
      this.handleError(e);
    }
  };

  getEstimateTotals = async (estimateId?: TEstimateId) => {
    if (!estimateId) return;
    try {
      const totals = await this.q(EstimateService.getEstimateTotals(estimateId));
      return totals;
    } catch (e) {
      this.handleError(e);
    }
  };

  refreshEstimateTotals = async (estimateId?: TEstimateId) => {
    if (!estimateId) return;
    try {
      const totals = await this.getEstimateTotals(estimateId);
      if (totals) {
        const estimateIndex = this.estimateList.findIndex((e) => e.id === estimateId);
        if (estimateIndex > -1) {
          const list = this.estimateList;
          list[estimateIndex] = {
            ...list[estimateIndex],
            total: totals.total,
            total_plus_fringe: totals.total_plus_fringe,
          };
          this.setEstimateList(list);
        }

        if (this.currentEstimate?.id === estimateId)
          runInAction(() => {
            this.setCurrentEstimate({
              ...this.currentEstimate,
              total: totals.total,
              total_plus_fringe: totals.total_plus_fringe,
            } as Estimate);
          });
      }
    } catch (e) {
      this.handleError(e);
    }
  };

  refreshEstimate = async (estimateId?: TEstimateId) => {
    if (!estimateId) return;
    try {
      const { estimate: updatedEstimate } = await this.q(EstimateService.getEstimate(estimateId, estimateRelations));
      if (!updatedEstimate) return;
      const estimateIndex = this.estimateList.findIndex((e) => e.id === estimateId);
      if (estimateIndex > -1) {
        const list = [...this.estimateList];
        list[estimateIndex] = updatedEstimate;
        this.setEstimateList(list);
      }

      if (this.currentEstimate?.id === estimateId) {
        runInAction(() => {
          this.setCurrentEstimate(updatedEstimate);
        });
      }
    } catch (e) {
      this.handleError(e);
    }
  };

  createEstimate = async (projectId: TProjectId, title?: string, isHidden?: boolean) => {
    if (!projectId) return;
    try {
      this.setIsSaving(true);
      const { estimate: newEstimate } = await this.q(
        EstimateService.createEstimate({
          projectId,
          title,
          isHidden,
        })
      );
      const estimate = await this.getEstimate(newEstimate?.id, estimateRelations);
      if (estimate) {
        runInAction(() => {
          this.estimateList.unshift(estimate);
        });
      }
      return estimate;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  recalculateEstimate = async (e: Estimate) => {
    const i = this.estimateList?.findIndex((p) => p.id === e.id);
    if (i === undefined || i === -1) return;
    // Every table on the screen is rebuilt
    const done = this.startRecalculating([
      ...(this.currentEstimate?.session_estimates ?? []).flatMap((se) => this.tableKeys('session', se.id)),
      ...(this.currentEstimate?.usage_estimates ?? []).flatMap((ue) => this.tableKeys('usage', ue.id)),
      recalcKey.estimate,
    ]);
    try {
      const { estimate } = await this.q(EstimateService.recalculateEstimate(e.id));
      const updatedEstimate = await this.getEstimate(estimate?.id, estimateRelations);
      if (updatedEstimate) {
        runInAction(() => {
          this.estimateList.splice(i, 1, updatedEstimate);
          this.setCurrentEstimate(updatedEstimate);
        });
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
    }
  };

  updateEstimate = async (e: Estimate) => {
    const i = this.estimateList?.findIndex((p) => p.id === e.id);
    if (i === undefined || i === -1) return;
    const updateRequest: TUpdateEstimateRequest = {
      status: e.status,
      title: e.title,
    };
    try {
      this.setIsSaving(true);
      const { estimate } = await this.q(EstimateService.updateEstimate(e.id, updateRequest));
      const updatedEstimate = await this.getEstimate(estimate?.id, estimateRelations);
      if (updatedEstimate) {
        runInAction(() => {
          this.estimateList.splice(i, 1, updatedEstimate);
          this.setCurrentEstimate(updatedEstimate);
        });
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteEstimate = async (estimateId: string) => {
    try {
      this.setIsSaving(true);
      await this.q(EstimateService.deleteEstimate(estimateId));
      if (estimateId === this.currentEstimate?.id) this.setCurrentEstimate(undefined);

      runInAction(() => {
        this.estimateList = this.estimateList?.filter((p) => p.id !== estimateId);
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  quickEstimate = async (
    performerCounts: Record<string, number>,
    workHours: number,
    hasPriorDayFitting: boolean,
    isVoiceOver: boolean,
    // A chosen contract, or the one in effect today (null)
    rateScheduleId: string | null = null
  ) => {
    try {
      const hasPerformerCounts = Object.values(performerCounts).findIndex((count) => count > 0) > -1;

      let total = 0;
      let totalPlusFringe = 0;

      if (hasPerformerCounts && workHours) {
        const res = await this.q(
          SessionEstimateService.quickSessionEstimate({
            performerCounts: JSON.stringify(performerCounts),
            workHours,
            hasPriorDayFitting,
            isVoiceOver,
            rateScheduleId: rateScheduleId ?? undefined,
          })
        );

        total = res.total;
        totalPlusFringe = res.totalPlusFringe;
        runInAction(() => (this.quickEstimateRateScheduleId = res.rateScheduleId));
      }

      runInAction(() => {
        this.quickEstimateTotal = total;
        this.quickEstimateTotalPlusFringe = totalPlusFringe;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  /**
   * Temp Estimates
   *
   * - Hidden Estimates for each Project
   * - Used for live estimation on the Session & Usage Estimators
   * - Created when the Estimators are used for the first time
   */

  setTempEstimate = (estimate?: Estimate) => (this.tempEstimate = estimate);

  getTempSessionEstimate = (sessionId?: TSessionId) => (sessionId ? this.tempSessionEstimates[sessionId] : undefined);
  getTempSessionEstimateItems = (sessionId?: TSessionId, activityId?: TSessionActivityId) =>
    sessionId && activityId ? this.tempSessionEstimateItems[sessionId]?.[activityId] : undefined;

  getTempUsageEstimate = (commercialId?: TCommercialId) =>
    commercialId ? this.tempUsageEstimates[commercialId] : undefined;
  getTempUsageEstimateItems = (commercialId?: TCommercialId, usageId?: TUsageId) =>
    commercialId && usageId ? this.tempUsageEstimateItems[commercialId]?.[usageId] : undefined;

  refreshTempEstimate = async (skipSessions = false, skipUsages = false) => {
    if (!this.tempEstimate || this.isLoading) return;
    try {
      runInAction(() => (this.isLoading = true));

      const estimateId = this.tempEstimate.id;
      const { estimate: updatedEstimate } = await this.q(EstimateService.getEstimate(estimateId, estimateRelations));
      if (!updatedEstimate) return;

      if (!skipSessions) {
        for (const sessionEstimate of updatedEstimate.session_estimates) {
          const session = sessionEstimate.session;
          if (!session) continue;

          const estimateItems = getSessionActivityItems(session, sessionEstimate);

          runInAction(() => {
            this.tempSessionEstimates[session.id] = sessionEstimate;
            this.tempSessionEstimateItems[session.id] = estimateItems;
          });
        }
      }

      if (!skipUsages) {
        const estimateItems: Record<TCommercialId, Record<TUsageId, UsageEstimateItem[]>> = {};
        for (const usageEstimate of updatedEstimate.usage_estimates) {
          if (!usageEstimate.commercial) continue;

          estimateItems[usageEstimate.commercial.id] ??= {};

          for (const item of usageEstimate.items) {
            estimateItems[usageEstimate.commercial.id][item.usage.id] ??= [];
            estimateItems[usageEstimate.commercial.id][item.usage.id].push(item);
          }

          runInAction(() => {
            if (usageEstimate.commercial) this.tempUsageEstimates[usageEstimate.commercial.id] = usageEstimate;
          });
        }

        runInAction(() => {
          this.tempUsageEstimateItems = estimateItems;
        });
      }

      runInAction(() => {
        this.tempEstimate = updatedEstimate;
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  /**
   * SessionEstimates
   */

  get lastSessionEstimateDate(): DateString | undefined {
    return (
      this.currentEstimate?.session_estimates.slice().sort((a, b) => ((a.date ?? '') > (b.date ?? '') ? -1 : 1))[0]
        ?.date ?? ''
    );
  }

  getSessionEstimate = async (estimateId?: TEstimateId, relations?: string) => {
    if (!estimateId) return;
    try {
      const { estimate } = await this.q(EstimateService.getEstimate(estimateId, relations));
      return estimate as Estimate;
    } catch (e) {
      this.handleError(e);
    }
  };

  getSessionEstimateDates = (sessionEstimateId?: TSessionEstimateId): DateString[] => {
    return (this.currentEstimate?.session_estimates
      .filter((se) => se.id !== sessionEstimateId)
      .slice()
      .sort((a, b) => ((a.date ?? '') > (b.date ?? '') ? -1 : 1))
      .map((se) => se.date ?? '') ?? []) as DateString[];
  };

  createSessionEstimate = async (
    title = 'New SessionEstimate',
    date = new Date().toUTCString(),
    sessionId?: TSessionId,
    // estimateId?: TEstimateId
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(
        SessionEstimateService.createSessionEstimate({
          estimateId: estimate.id,
          title,
          date,
          sessionId,
        })
      );

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateSessionEstimate = async (
    sessionEstimateId: TSessionEstimateId,
    title?: string,
    date?: Nullable<string>,
    sessionId?: TSessionId,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot update SessionEstimate');
      return;
    }

    const done = this.startRecalculating(this.tableKeys('session', sessionEstimateId));
    try {
      this.setIsSaving(true);
      await this.q(
        SessionEstimateService.updateSessionEstimate(sessionEstimateId, {
          title,
          date: date ?? undefined,
          sessionId,
        })
      );

      isTempEstimate ? await this.refreshTempEstimate() : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  /**
   * Contracts
   */

  // Once per page load, however many dropdowns ask at once
  loadRateSchedules = () => {
    this.rateSchedulesRequest ??= (async () => {
      try {
        const { rateSchedules } = await this.q(RateScheduleService.listRateSchedules());
        runInAction(() => (this.rateSchedules = rateSchedules));
      } catch (e) {
        this.rateSchedulesRequest = undefined;
        this.handleError(e);
      }
    })();
    return this.rateSchedulesRequest;
  };

  /** Prices a session estimate with a chosen contract, or with the automatic one (null) */
  setSessionEstimateContract = async (sessionEstimateId: TSessionEstimateId, rateScheduleId: string | null) => {
    const estimate = this.currentEstimate;
    if (!estimate) return;
    const done = this.startRecalculating(this.tableKeys('session', sessionEstimateId));
    try {
      this.setIsSaving(true);
      await this.q(
        SessionEstimateService.updateSessionEstimate(sessionEstimateId, { rateScheduleOverrideId: rateScheduleId })
      );
      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  /** Set every session item value edited by hand back to the contract's */
  resetSessionEstimateEdits = async (sessionEstimateId: TSessionEstimateId) => {
    const estimate = this.currentEstimate;
    if (!estimate) return;
    const done = this.startRecalculating(this.tableKeys('session', sessionEstimateId));
    try {
      this.setIsSaving(true);
      await this.q(SessionEstimateService.updateSessionEstimate(sessionEstimateId, { resetItemEdits: true }));
      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  /** Set every usage item value edited by hand back to the contract's */
  resetUsageEstimateEdits = async (usageEstimateId: TUsageEstimateId) => {
    const estimate = this.currentEstimate;
    if (!estimate) return;
    const done = this.startRecalculating(this.tableKeys('usage', usageEstimateId));
    try {
      this.setIsSaving(true);
      await this.q(UsageEstimateService.updateUsageEstimate(usageEstimateId, { resetItemEdits: true }));
      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  /** Prices a usage estimate with a chosen contract, or with the automatic one (null) */
  setUsageEstimateContract = async (usageEstimateId: TUsageEstimateId, rateScheduleId: string | null) => {
    const estimate = this.currentEstimate;
    if (!estimate) return;
    const done = this.startRecalculating(this.tableKeys('usage', usageEstimateId));
    try {
      this.setIsSaving(true);
      await this.q(
        UsageEstimateService.updateUsageEstimate(usageEstimateId, { rateScheduleOverrideId: rateScheduleId })
      );
      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  deleteSessionEstimate = async (sessionEstimateId: TSessionEstimateId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(SessionEstimateService.deleteSessionEstimate(sessionEstimateId));

      isTempEstimate ? await this.refreshTempEstimate() : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**
   * SessionEstimateItems
   */

  createSessionEstimateItem = async (
    sessionEstimateId: TSessionEstimateId,
    title = 'New Estimate Item',
    units = 1,
    unitType = SessionEstimateItemUnitType.Count,
    rate = 0,
    sessionActivityId?: TSessionActivityId,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    const done = this.startRecalculating([recalcKey.table('session', sessionEstimateId), recalcKey.estimate]);
    try {
      this.setIsSaving(true);
      await SessionEstimateItemService.createSessionEstimateItem({
        sessionEstimateId,
        sessionActivityId,
        title,
        units,
        unitType,
        rate,
      });

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  updateSessionEstimateItem = async (
    sessionEstimateItemId: TSessionEstimateItemId,
    title?: string,
    units?: number,
    unitType?: SessionEstimateItemUnitType,
    rate?: number,
    taxesMultiplier?: number,
    handlingMultiplier?: number,
    pAndHMultiplier?: number,
    sessionActivityId?: TSessionActivityId,
    isTempEstimate?: boolean,
    // Values edited by hand to set back to the contract's
    { resetToDefault }: Pick<TUpdateSessionEstimateItemRequest, 'resetToDefault'> = {}
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

    if (!estimate) {
      console.error('No saved Estimate - cannot create SessionEstimate');
      return;
    }

    const done = this.startRecalculating(this.itemKeys('session', sessionEstimateItemId, !!resetToDefault?.length));
    try {
      this.setIsSaving(true);
      await SessionEstimateItemService.updateSessionEstimateItem(sessionEstimateItemId, {
        resetToDefault,
        title,
        units,
        unitType,
        rate,
        taxesMultiplier,
        handlingMultiplier,
        pAndHMultiplier,
        sessionActivityId,
      });

      await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  deleteSessionEstimateItem = async (sessionEstimateItemId: TSessionEstimateItemId, isTempEstimate?: boolean) => {
    const done = this.startRecalculating(this.itemKeys('session', sessionEstimateItemId));
    let deleted = false;
    try {
      this.setIsSaving(true);
      const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;

      if (!estimate) {
        console.error('No saved Estimate - cannot create SessionEstimate');
        return false;
      }

      await SessionEstimateItemService.deleteSessionEstimateItem(sessionEstimateItemId);
      deleted = true;

      await this.refreshEstimate(estimate.id);
      return true;
    } catch (e) {
      this.handleError(e, deleted ? 'Item deleted, but the estimate could not refresh. Reload to update totals.' : undefined);
      return deleted;
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  /**
   * UsageEstimates
   */

  get usageEstimateList(): UsageEstimate[] | undefined {
    return this.currentEstimate?.usage_estimates;
  }

  createUsageEstimate = async (title = 'New UsageEstimate', commercialId?: TCommercialId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot create UsageEstimate');
      return;
    }

    try {
      await this.q(
        UsageEstimateService.createUsageEstimate({
          estimateId: estimate.id,
          title,
          commercialId,
        })
      );

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    }
  };

  updateUsageEstimate = async (usageEstimateId: TUsageEstimateId, title?: string, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot update UsageEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(
        UsageEstimateService.updateUsageEstimate(usageEstimateId, {
          title,
        })
      );

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteUsageEstimate = async (usageEstimateId: TUsageEstimateId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot delete UsageEstimate');
      return;
    }

    try {
      this.setIsSaving(true);
      await this.q(UsageEstimateService.deleteUsageEstimate(usageEstimateId));

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**
   * Usage Estimate Items
   */

  createUsageEstimateItem = async (newUsageEstimateItem: TCreateUsageEstimateItemRequest, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot create UsageEstimateItem');
      return;
    }

    const done = this.startRecalculating([
      recalcKey.table('usage', newUsageEstimateItem.usageEstimateId),
      recalcKey.estimate,
    ]);
    try {
      this.setIsSaving(true);
      await UsageEstimateItemService.createUsageEstimateItem(newUsageEstimateItem);

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  updateUsageEstimateItem = async (
    usageEstimateItemId: TUsageEstimateItemId,
    updatedUsageEstimateItem: TUpdateUsageEstimateItemRequest,
    isTempEstimate?: boolean
  ) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot update UsageEstimateItem');
      return;
    }

    const done = this.startRecalculating(
      this.itemKeys('usage', usageEstimateItemId, this.repricesRate(usageEstimateItemId, updatedUsageEstimateItem))
    );
    try {
      this.setIsSaving(true);
      const { usageEstimateItem } = await this.q(
        UsageEstimateItemService.updateUsageEstimateItem(usageEstimateItemId, updatedUsageEstimateItem)
      );

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);

      return usageEstimateItem;
    } catch (e) {
      this.handleError(e);
    } finally {
      done();
      this.setIsSaving(false);
    }
  };

  deleteUsageEstimateItem = async (usageEstimateItemId: TUsageEstimateItemId, isTempEstimate?: boolean) => {
    const estimate = isTempEstimate ? this.tempEstimate : this.currentEstimate;
    if (!estimate) {
      console.error('No saved Estimate - cannot delete UsageEstimateItem');
      return false;
    }

    const done = this.startRecalculating(this.itemKeys('usage', usageEstimateItemId));
    let deleted = false;
    try {
      this.setIsSaving(true);
      await UsageEstimateItemService.deleteUsageEstimateItem(usageEstimateItemId);
      deleted = true;

      isTempEstimate ? await this.refreshTempEstimate(true) : await this.refreshEstimate(estimate.id);
      return true;
    } catch (e) {
      this.handleError(e, deleted ? 'Item deleted, but the estimate could not refresh. Reload to update totals.' : undefined);
      return deleted;
    } finally {
      done();
      this.setIsSaving(false);
    }
  };
}
